/**
 * VerificationToolbar — run trigger + result summary (F6-6).
 *
 * lawyer/reviewer get the 「发起来源核对」 button; viewer sees the summary only
 * (PRD §2 read-only). The distribution counts the *current* verdict list by
 * composite status so the operator sees coverage at a glance.
 */
import * as React from 'react';
import type {
  VerdictOverall,
  VerificationResult,
  VerificationSummary,
} from '../../lib/veridoc-api';

import { OVERALL_LABELS } from './constants';
import { OVERALL_TONE, TONE_CLASSES } from './verdictVisual';

const OVERALL_ORDER: VerdictOverall[] = [
  'fail',
  'warning',
  'unverifiable',
  'pass',
];

function distribution(results: VerificationResult[]): Record<VerdictOverall, number> {
  const counts: Record<VerdictOverall, number> = {
    pass: 0,
    warning: 0,
    fail: 0,
    unverifiable: 0,
  };
  for (const r of results) counts[r.payload.verdict.overall] += 1;
  return counts;
}

export function VerificationToolbar({
  results,
  lastRun,
  running,
  canRun,
  onRun,
}: {
  results: VerificationResult[];
  lastRun: VerificationSummary | null;
  running: boolean;
  canRun: boolean;
  onRun: () => void;
}): React.JSX.Element {
  const counts = distribution(results);
  const hasResults = results.length > 0;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-200 bg-ink-0 px-4 py-3 shadow-sm">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="text-sm font-semibold text-ink-900">来源核对</h2>
        {hasResults ? (
          <>
            <span className="text-xs text-ink-500">共 {results.length} 条引用</span>
            <span className="text-ink-300">·</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {OVERALL_ORDER.filter((o) => counts[o] > 0).map((o) => (
                <span
                  key={o}
                  className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs ${
                    TONE_CLASSES[OVERALL_TONE[o]].pill
                  }`}
                >
                  {OVERALL_LABELS[o]} {counts[o]}
                </span>
              ))}
            </div>
          </>
        ) : (
          <span className="text-xs text-ink-500">尚无核验结论</span>
        )}
        {lastRun && lastRun.failedCount > 0 && (
          <span className="text-xs text-semantic-warning">
            · {lastRun.failedCount} 条降级
          </span>
        )}
      </div>

      {canRun && (
        <button
          type="button"
          onClick={onRun}
          disabled={running}
          className="inline-flex h-btn-sm shrink-0 items-center gap-1.5 rounded-sm border border-brand-600 bg-brand-600 px-3 text-xs font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {running ? (
            <>
              <Spinner />
              正在核验…
            </>
          ) : (
            <>
              <span aria-hidden="true">⟳</span>
              {hasResults ? '重新核验' : '发起来源核对'}
            </>
          )}
        </button>
      )}
    </div>
  );
}

function Spinner(): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
    />
  );
}

export default VerificationToolbar;
